import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { laundryAPI, getApiErrorMessage, type DeliveryBoardOrderDto, type LaundryBoardDto } from '@matix/api-client';
import { useLang } from '../../lib/language';
import { fmt } from '../../lib/mock-data';
import { toast } from '../../store/toast';
import { useCart } from '../../store/cart';
import { openHeldOrder } from '../held-orders/openHeld';
import { DispatchDialog } from '../delivery-manager/DispatchDialog';
import { customerOf, formatStamp, matches, ORDER_TYPE, orderNo, STATUS, wallMs } from '../delivery-manager/model';
import { useLaundryPrint } from './useLaundryPrint';
import './laundry.css';
// The bilingual manual (docs/laundry). Imported so the build fingerprints it
// into /assets/ — the hosts' SPA fallback answers any other static path with
// index.html.
import manualUrl from '../../../../../docs/laundry/Matix-Laundry-Manual-EN-AR.pdf?url';

/**
 * The LAUNDRY counter (module `laundry`): every order taken in and not yet
 * collected, plus today's collections, by status —
 *
 *   Received → Ready (or Waiting for pickup) → Out for delivery → Collected
 *
 * The scan box reads the order barcode printed on the ticket and the bag tag
 * (the invoice number) and opens that order. Collect reopens the held invoice
 * in the cart with the payment window up; Charge then pays it AND marks it
 * Delivered in one server transaction (laundryAPI.collect).
 */
type Column = 'received' | 'ready' | 'out' | 'collected';

/** The till's local wall clock as a UTC-axis millisecond count (see wallMs). */
function localWallNow(): number {
  return Date.now() - new Date().getTimezoneOffset() * 60_000;
}

/**
 * A held order (pay at pickup) and a PAID one (pay-now, stamped by the server)
 * walk the same columns; only the last step differs — Collect & pay vs Hand
 * over. The server lists a paid order only while it is a laundry order.
 */
function columnOf(o: DeliveryBoardOrderDto): Column | null {
  if (o.status === STATUS.Delivered) return 'collected';
  if (o.status === STATUS.New) return 'received';
  if (o.status === STATUS.Ready || o.status === STATUS.Waiting) return 'ready';
  if (o.status === STATUS.OnTheWay) return 'out';
  return null;
}

export function LaundryScreen(): JSX.Element {
  const { t, lang } = useLang();
  const [board, setBoard] = useState<LaundryBoardDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [q, setQ] = useState('');
  const [scan, setScan] = useState('');
  const [highlight, setHighlight] = useState<number | null>(null);
  const [dispatchFor, setDispatchFor] = useState<DeliveryBoardOrderDto | null>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const printTicket = useLaundryPrint();
  // Ready-by dates are the shop's wall clock (naive), read as UTC by wallMs —
  // so compare against the till's local wall clock on the same axis.
  const [now, setNow] = useState(localWallNow);
  useEffect(() => {
    const id = window.setInterval(() => setNow(localWallNow()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBoard(await laundryAPI.board());
    } catch (err) {
      toast(getApiErrorMessage(err) || t({ en: 'Could not load laundry orders', ar: 'تعذّر تحميل طلبات المغسلة' }), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
    scanRef.current?.focus();
    const id = window.setInterval(() => void load(), 30_000);
    return () => window.clearInterval(id);
  }, [load]);

  const columns = useMemo(() => {
    const out: Record<Column, DeliveryBoardOrderDto[]> = { received: [], ready: [], out: [], collected: [] };
    for (const o of board?.orders ?? []) {
      const c = columnOf(o);
      if (c && matches(o, q)) out[c].push(o);
    }
    // Most urgent first: the earliest ready-by date.
    for (const k of ['received', 'ready', 'out'] as const) {
      out[k].sort((a, b) => (wallMs(a.delivery_date) ?? 0) - (wallMs(b.delivery_date) ?? 0));
    }
    out.collected.sort((a, b) => (wallMs(b.delivered_at) ?? 0) - (wallMs(a.delivered_at) ?? 0));
    return out;
  }, [board, q]);

  /** The scan box: a barcode scanner types the number and Enter. */
  const onScan = async () => {
    const code = scan.trim();
    if (!code) return;
    setScan('');
    try {
      const order = await laundryAPI.lookup(code);
      if (!order) {
        toast(t({ en: `No laundry order for ${code}`, ar: `لا يوجد طلب مغسلة للرقم ${code}` }), 'warning');
        return;
      }
      setQ('');
      setHighlight(order.id);
      if (!board?.orders.some((o) => o.id === order.id)) await load();
      window.setTimeout(() => document.getElementById(`laundry-card-${order.id}`)?.scrollIntoView?.({ block: 'center' }), 50);
    } catch (err) {
      toast(getApiErrorMessage(err) || String(err), 'error');
    } finally {
      scanRef.current?.focus();
    }
  };

  const move = async (o: DeliveryBoardOrderDto, action: 'ready' | 'dispatch', driverId?: number | null) => {
    setBusyId(o.id);
    try {
      await laundryAPI.status(o.id, { action, expected_status: o.status, ...(driverId ? { driver_id: driverId } : {}) });
      setDispatchFor(null);
      await load();
    } catch (err) {
      toast(getApiErrorMessage(err) || String(err), 'error');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const collect = async (o: DeliveryBoardOrderDto) => {
    setBusyId(o.id);
    try {
      const opened = await openHeldOrder(o.id, { pay: true, tr: t });
      // Charge now pays it AND marks it Delivered (laundryAPI.collect).
      if (opened) useCart.getState().setLaundryCollectStatus(o.status);
    } finally {
      setBusyId(null);
    }
  };

  /** A paid (pay-now) order: nothing to charge — Delivered on the spot. */
  const handOver = async (o: DeliveryBoardOrderDto) => {
    setBusyId(o.id);
    try {
      await laundryAPI.handOver(o.id, { expected_status: o.status });
      toast(`${t({ en: 'Handed over', ar: 'تم التسليم' })} · ${orderNo(o)}`, 'success');
      await load();
    } catch (err) {
      toast(getApiErrorMessage(err) || String(err), 'error');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const count = (c: Column) => columns[c].length;
  const COLS: Array<{ key: Column; title: { en: string; ar: string } }> = [
    { key: 'received', title: { en: 'Received', ar: 'مستلم' } },
    { key: 'ready', title: { en: 'Ready', ar: 'جاهز' } },
    { key: 'out', title: { en: 'Out for delivery', ar: 'خرج للتوصيل' } },
    { key: 'collected', title: { en: 'Collected today', ar: 'سُلّم اليوم' } },
  ];

  const card = (o: DeliveryBoardOrderDto, col: Column) => {
    const due = wallMs(o.delivery_date);
    const late = col !== 'collected' && col !== 'ready' && due != null && now > due;
    const isDelivery = o.order_type === ORDER_TYPE.Delivery;
    const busy = busyId === o.id;
    return (
      <div
        key={o.id}
        id={`laundry-card-${o.id}`}
        className={`laundry-card${late ? ' is-late' : ''}${highlight === o.id ? ' is-hit' : ''}`}
      >
        <div className="laundry-card-top">
          <span className="laundry-no">{orderNo(o)}</span>
          <span className="laundry-amount">{fmt(o.amount)}</span>
        </div>
        <div className="laundry-customer" dir="auto">
          {customerOf(o, lang) || t({ en: 'Walk-in', ar: 'عميل نقدي' })}
          {o.customer_phone && <span className="laundry-phone"> · {o.customer_phone}</span>}
        </div>
        <div className="laundry-meta">
          {col === 'collected' ? (
            <span>{t({ en: 'Collected', ar: 'سُلّم' })} {formatStamp(o.delivered_at, lang)}</span>
          ) : (
            <span className={late ? 'laundry-late' : ''}>
              {t({ en: 'Ready by', ar: 'جاهز في' })} {formatStamp(o.delivery_date, lang)}
              {late ? ` · ${t({ en: 'late', ar: 'متأخر' })}` : ''}
            </span>
          )}
          {isDelivery && <span className="laundry-tag">{t({ en: 'Delivery', ar: 'توصيل' })}</span>}
          {!o.is_held && col !== 'collected' && (
            <span className="laundry-tag laundry-paid">{t({ en: 'Paid', ar: 'مدفوع' })}</span>
          )}
          {o.driver_name && col === 'out' && <span className="laundry-tag">{lang === 'ar' ? o.driver_name_ar || o.driver_name : o.driver_name}</span>}
        </div>
        {col !== 'collected' && (
          <div className="laundry-actions">
            <button type="button" className="btn-secondary laundry-btn" disabled={busy} onClick={() => void printTicket(o.id)}>
              {t({ en: 'Print', ar: 'طباعة' })}
            </button>
            {col === 'received' && (
              <button type="button" className="btn-primary laundry-btn" disabled={busy} onClick={() => void move(o, 'ready')}>
                {t({ en: 'Mark ready', ar: 'تحديد كجاهز' })}
              </button>
            )}
            {col === 'ready' && isDelivery && o.status === STATUS.Ready && (
              <button type="button" className="btn-primary laundry-btn" disabled={busy} onClick={() => setDispatchFor(o)}>
                {t({ en: 'Send out', ar: 'إرسال للتوصيل' })}
              </button>
            )}
            {(col === 'out' || (col === 'ready' && !isDelivery)) && (
              o.is_held ? (
                <button type="button" className="btn-primary laundry-btn" disabled={busy} onClick={() => void collect(o)}>
                  {t({ en: 'Collect & pay', ar: 'تسليم ودفع' })}
                </button>
              ) : (
                <button type="button" className="btn-primary laundry-btn" disabled={busy} onClick={() => void handOver(o)}>
                  {t({ en: 'Hand over', ar: 'تسليم' })}
                </button>
              )
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="laundry-screen">
      <div className="laundry-head">
        <div className="laundry-title">{t({ en: 'Laundry orders', ar: 'طلبات المغسلة' })}</div>
        <input
          ref={scanRef}
          className="laundry-scan"
          value={scan}
          onChange={(e) => setScan(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void onScan(); }}
          placeholder={t({ en: 'Scan the order barcode…', ar: 'امسح باركود الطلب…' })}
          inputMode="numeric"
          aria-label={t({ en: 'Scan the order barcode', ar: 'امسح باركود الطلب' })}
        />
        <input
          className="laundry-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t({ en: 'Search number, customer, phone…', ar: 'بحث بالرقم أو العميل أو الهاتف…' })}
        />
        <a className="btn-secondary laundry-manual" href={manualUrl} target="_blank" rel="noreferrer">
          {t({ en: 'User manual', ar: 'دليل المستخدم' })}
        </a>
        <button type="button" className="btn-secondary" onClick={() => void load()} disabled={loading}>
          {loading ? t({ en: 'Loading…', ar: 'جارٍ التحميل…' }) : t({ en: 'Refresh', ar: 'تحديث' })}
        </button>
      </div>

      <div className="laundry-cols">
        {COLS.map((c) => (
          <section key={c.key} className={`laundry-col laundry-col--${c.key}`}>
            <header className="laundry-col-head">
              <span>{t(c.title)}</span>
              <span className="laundry-count">{count(c.key)}</span>
            </header>
            <div className="laundry-col-body">
              {columns[c.key].length === 0 ? (
                <div className="laundry-empty">{t({ en: 'Nothing here', ar: 'لا يوجد' })}</div>
              ) : (
                columns[c.key].map((o) => card(o, c.key))
              )}
            </div>
          </section>
        ))}
      </div>

      {dispatchFor && board && (
        <DispatchDialog
          order={dispatchFor}
          drivers={board.drivers}
          chefs={board.chefs}
          busy={busyId === dispatchFor.id}
          onConfirm={(driverId) => void move(dispatchFor, 'dispatch', driverId)}
          onClose={() => setDispatchFor(null)}
        />
      )}
    </div>
  );
}
